import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { SOC } from '../../constants/company';
import { DEVISE, fmt } from '../../lib/currency';
import { MOYENS, libelleTaux } from '../../lib/caisse';
import { printDoc } from '../../lib/print';
import { C } from '../../theme/palette';

const ligneStyle = { display: 'flex', justifyContent: 'space-between' };

/**
 * Dit si la caisse a noté le montant remis par le client.
 *
 * @param {object} ticket le ticket
 * @returns {boolean} vrai si un montant reçu, non nul, a été saisi
 */
const montantRecuSaisi = (ticket) => ticket.recu != null && (+ticket.recu || 0) > 0;

/**
 * Ticket de caisse imprimable : une vente, ou l'avoir qui la rembourse.
 *
 * <p>Le ticket et l'avoir portent les mêmes mentions — société, numéro de TVA,
 * numéro et date, sous-total hors taxe, une ligne par taux avec son assiette,
 * total TTC — parce que l'AFC attend les mêmes de l'un et de l'autre. L'avoir
 * s'écrit en négatif et nomme le ticket qu'il rembourse.
 *
 * @param {object} props propriétés
 * @param {object} props.ticket le ticket à imprimer
 * @param {object} props.soc société émettrice
 * @param {Function} props.onFermer ferme le ticket
 * @returns {import('react').ReactElement} le ticket
 */
export function TicketComptoir({ ticket, soc, onFermer }) {
  const avoir = !!ticket.avoir;
  const ventilation =
    ticket.ventilation && ticket.ventilation.length > 0
      ? ticket.ventilation
      : [{ taux: ticket.tvaTaux, ht: ticket.ht, tva: ticket.tva }];
  return (
    <Card style={{ marginBottom: 11, border: '2px solid ' + (avoir ? C.amber : C.green) }}>
      <div style={{ padding: 13 }}>
        <style>
          {
            '@media print{body *{visibility:hidden}#ticket-caisse,#ticket-caisse *{visibility:visible}#ticket-caisse{position:absolute;left:0;top:0;width:100%}.noprint-tk{display:none!important}}'
          }
        </style>
        <div
          id="ticket-caisse"
          data-avoir={avoir ? '1' : '0'}
          style={{
            fontFamily: 'monospace',
            maxWidth: 300,
            margin: '0 auto',
            background: '#fff',
            color: '#111',
            padding: '12px 14px',
            border: '1px solid ' + C.border,
            borderRadius: 6,
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 900 }}>{soc?.nom || SOC.nom}</div>
            <div style={{ fontSize: 8 }}>{(soc?.adr || '') + ' ' + (soc?.npa || '')}</div>
            {soc?.tva && (
              <div style={{ fontSize: 8 }}>
                {/* Le numéro s'écrit souvent avec « TVA » (CHE-… TVA) : pas deux fois. */}
                {/TVA|MWST|IVA/i.test(soc.tva) ? soc.tva : 'TVA ' + soc.tva}
              </div>
            )}
          </div>
          {avoir && (
            <div style={{ textAlign: 'center', fontWeight: 900, fontSize: 9, marginBottom: 4 }}>
              AVOIR — RETOUR
            </div>
          )}
          <div
            style={{
              fontSize: 8.5,
              borderTop: '1px dashed #999',
              borderBottom: '1px dashed #999',
              padding: '4px 0',
              marginBottom: 6,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>{ticket.num}</span>
            <span>
              {ticket.date} {ticket.heure}
            </span>
          </div>
          {avoir && (
            <div style={{ fontSize: 8.5, marginBottom: 4 }}>
              Ticket d'origine : {ticket.ticketOrigine || '—'}
              {ticket.motif ? <div>Motif : {ticket.motif}</div> : null}
            </div>
          )}
          <div style={{ fontSize: 8.5, marginBottom: 6 }}>Client : {ticket.clientNom}</div>
          <div
            style={{
              fontSize: 8.5,
              color: '#666',
              display: 'flex',
              justifyContent: 'space-between',
              borderBottom: '1px solid #ddd',
              paddingBottom: 2,
              marginBottom: 3,
            }}
          >
            <span>Article</span>
            <span>Prix HT</span>
          </div>
          {ticket.lignes.map((l, i) => (
            <div key={i} style={{ ...ligneStyle, fontSize: 9, marginBottom: 2 }}>
              <span>
                {l.q} × {l.nom}
                {l.rem > 0 ? ' (-' + l.rem + '%)' : ''}
              </span>
              <span>{fmt(l.net)}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px dashed #999', marginTop: 6, paddingTop: 6, fontSize: 9 }}>
            {ticket.remiseGlob > 0 && (
              <div style={{ ...ligneStyle, color: '#b45309' }}>
                <span>Remise vente -{ticket.remiseGlob}%</span>
                <span>
                  -{fmt((ticket.totalArt || ticket.totalHt) - (ticket.totalHt ?? ticket.total))}
                </span>
              </div>
            )}
            <div style={ligneStyle}>
              <span>Sous-total HT</span>
              <span>{fmt(ticket.ht)}</span>
            </div>
            {/*
              Un panier peut meler une caisse d'eau a 2,6 %, un ustensile a
              8,1 % et un livre exonere : chaque taux se lit avec son assiette,
              c'est ce que l'AFC attend d'un ticket. Une seule ligne subsiste
              quand tout est au meme taux.
            */}
            {ventilation.map((v) => (
              <div key={v.taux} style={ligneStyle}>
                <span>
                  {libelleTaux(v)}
                  {ventilation.length > 1 || v.exonere ? (
                    <span style={{ color: '#666' }}> sur {fmt(v.ht)}</span>
                  ) : null}
                </span>
                <span>{fmt(v.tva)}</span>
              </div>
            ))}
            <div style={{ ...ligneStyle, fontWeight: 900, fontSize: 12, marginTop: 3 }}>
              <span>TOTAL TTC</span>
              <span>
                {DEVISE} {fmt(ticket.total)}
              </span>
            </div>
          </div>
          <div style={{ borderTop: '1px dashed #999', marginTop: 6, paddingTop: 6, fontSize: 9 }}>
            <div style={ligneStyle}>
              <span>{avoir ? 'Remboursement' : 'Paiement'}</span>
              <span>{MOYENS[ticket.pay] || ticket.pay}</span>
            </div>
            {/*
              Sans montant recu saisi, « Recu 0.00 / Rendu 0.00 » laissait
              croire que rien n'avait ete paye : les deux lignes se taisent.
            */}
            {!avoir && ticket.pay === 'especes' && montantRecuSaisi(ticket) && (
              <>
                <div style={ligneStyle}>
                  <span>Reçu</span>
                  <span>{fmt(ticket.recu)}</span>
                </div>
                <div style={{ ...ligneStyle, fontWeight: 800 }}>
                  <span>Rendu</span>
                  <span>{fmt(ticket.rendu)}</span>
                </div>
              </>
            )}
          </div>
          {ticket.pointsSolde != null && (
            <div
              style={{
                ...ligneStyle,
                borderTop: '1px dashed #999',
                marginTop: 6,
                paddingTop: 6,
                fontSize: 9,
              }}
            >
              <span>⭐ Points gagnés +{ticket.pointsGain}</span>
              <span>Solde : {ticket.pointsSolde}</span>
            </div>
          )}
          <div style={{ textAlign: 'center', fontSize: 8.5, marginTop: 8 }}>
            {avoir ? 'Avoir établi sur présentation du ticket' : 'Merci de votre visite !'}
          </div>
        </div>
        <div className="noprint-tk" style={{ display: 'flex', gap: 8, marginTop: 11 }}>
          <Btn
            label={avoir ? "🖨 Imprimer l'avoir" : '🖨 Imprimer le ticket'}
            color={C.navy}
            full
            onClick={printDoc}
          />
          <Btn label="Fermer" color={C.muted} onClick={onFermer} />
        </div>
      </div>
    </Card>
  );
}
